import React, { useCallback, useEffect, useState } from 'react';
import { QrCode, Download, X } from 'lucide-react';
import { QrisAccountListItem, QrisAccountStatus, QrisAccountType } from '../types';
import api from '../services/api';
import QrisRegistrationDetailSlideOver from '../components/QrisRegistrationDetailSlideOver';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/helpers';
import { QRIS_TYPE_LABEL, qrisBlobErrorMessage, qrisStatusBadge, saveBlob, todayStamp } from '../utils/qris';

const TABS: { label: string; status: QrisAccountStatus | '' }[] = [
  { label: 'Semua', status: '' },
  { label: 'Perlu Diekspor', status: 'submitted' },
  { label: 'Menunggu Paydia', status: 'uploaded' },
  { label: 'Aktif', status: 'active' },
  { label: 'Ditolak', status: 'rejected' },
  { label: 'Perlu Refund', status: 'refund_pending' },
];

const FILE_SLUG: Record<QrisAccountType, string> = { individual: 'perorangan', business: 'badan-usaha' };
const TYPES: QrisAccountType[] = ['individual', 'business'];

const QrisRegistrations: React.FC = () => {
  const { canManage } = useAuth();
  const [items, setItems] = useState<QrisAccountListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<QrisAccountStatus | ''>('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const perPage = 15;

  const [exportOpen, setExportOpen] = useState(false);
  const [counts, setCounts] = useState<Record<QrisAccountType, number> | null>(null);
  const [exporting, setExporting] = useState<QrisAccountType | null>(null);
  const [exportError, setExportError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getQrisAccounts({ page, perPage, status: status || undefined, search: search || undefined });
      setItems(res.data);
      setTotal(res.meta.total);
    } catch (e) {
      console.error('Error loading QRIS registrations:', e);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, status, search]);

  useEffect(() => { load(); }, [load]);

  const openExport = async () => {
    setExportOpen(true);
    setExportError('');
    setCounts(null);
    try {
      const [individual, business] = await Promise.all(TYPES.map((type) => api.getQrisAccounts({ page: 1, perPage: 1, status: 'submitted', type })));
      setCounts({ individual: individual.meta.total, business: business.meta.total });
    } catch {
      setExportError('Gagal memeriksa jumlah pendaftaran yang perlu diekspor.');
    }
  };

  const doExport = async (type: QrisAccountType) => {
    if (exporting) return;
    try {
      setExporting(type);
      setExportError('');
      const blob = await api.exportQrisAccounts(type);
      saveBlob(blob, `paydia-import-${FILE_SLUG[type]}-${todayStamp()}.xlsx`);
      setExportOpen(false);
      await load();
    } catch (err: any) {
      setExportError(await qrisBlobErrorMessage(err, 'Gagal mengekspor.'));
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <QrCode className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Pendaftaran QRIS</h1>
        </div>
        {canManage && (
          <button onClick={openExport} className="inline-flex items-center gap-1 bg-primary-600 text-white rounded-md px-4 py-2 hover:bg-primary-700">
            <Download className="h-4 w-4" /> Ekspor ke Template Paydia
          </button>
        )}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((tab) => (
          <button key={tab.label} onClick={() => { setPage(1); setStatus(tab.status); }}
            className={`px-3 py-1.5 rounded-full text-sm border ${status === tab.status ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mb-4">
        <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Cari merchant, email, MID, usaha..."
          className="w-full sm:w-96 border border-gray-300 rounded-md px-3 py-2" />
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-2">Merchant</th><th className="px-4 py-2">Company</th><th className="px-4 py-2">Jenis</th>
                <th className="px-4 py-2">Status</th><th className="px-4 py-2">Ditolak</th><th className="px-4 py-2">Kredensial</th>
                <th className="px-4 py-2">MID</th><th className="px-4 py-2">Dikirim</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((a) => (
                <tr key={a.id} onClick={() => setDetailId(a.id)} className="cursor-pointer hover:bg-gray-50">
                  <td className="px-4 py-2">
                    {a.merchantName || a.registrationMerchantName || '-'}
                    {a.registrationEmail && <div className="text-xs text-gray-500">{a.registrationEmail}</div>}
                  </td>
                  <td className="px-4 py-2">{a.company.brandName || a.company.name}</td>
                  <td className="px-4 py-2">{QRIS_TYPE_LABEL[a.type]}</td>
                  <td className="px-4 py-2">{qrisStatusBadge(a.status)}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{a.rejectionCount}/{a.maxRejections}</td>
                  <td className="px-4 py-2">{a.hasCredentials ? 'Tersimpan' : 'Dihapus'}</td>
                  <td className="px-4 py-2 font-mono">{a.mid ?? '-'}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{a.submittedAt ? formatDate(a.submittedAt) : '-'}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Belum ada pendaftaran QRIS.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4">
        <Pagination currentPage={page} total={total} perPage={perPage} onPageChange={setPage} />
      </div>

      <QrisRegistrationDetailSlideOver accountId={detailId} onClose={() => setDetailId(null)} onChanged={load} />

      {canManage && exportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Ekspor ke Template Paydia</h2>
              <button onClick={() => !exporting && setExportOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-3 text-sm">
              <div className="p-3 rounded-md bg-yellow-50 border border-yellow-200 text-yellow-900">
                File memuat kata sandi &amp; PIN. Hapus setelah diunggah ke Paydia.
              </div>
              <p className="text-gray-600">Hanya pendaftaran berstatus Perlu Diekspor yang disertakan.</p>
              {TYPES.map((type) => {
                const count = counts?.[type];
                const empty = counts !== null && count === 0;
                return (
                  <div key={type}>
                    <button disabled={counts === null || empty || exporting !== null} onClick={() => doExport(type)}
                      className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                      <span className="font-medium">{QRIS_TYPE_LABEL[type]}</span>
                      <span className="text-gray-500">{exporting === type ? 'Mengekspor...' : counts === null ? '...' : `${count} pendaftaran`}</span>
                    </button>
                    {empty && <p className="mt-1 text-xs text-gray-500">Tidak ada pendaftaran yang perlu diekspor.</p>}
                  </div>
                );
              })}
              {exportError && <div className="p-3 bg-red-50 text-red-700 rounded-md">{exportError}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QrisRegistrations;
